import Styles from './AutopilotUpgradeModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { Pressable } from '@react-aria/interactions'
import { Lightning, Play3 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { HighDetailedScheduled } from 'blaze-ui/icons/24'
import { HighDetailedSocial } from 'blaze-ui/icons/40'
import HighDetailedImproveQuality from 'blaze-ui/icons/40/HighDetailedImproveQuality'
import { durationLabel } from 'components/VideoDuration/VideoDuration'
import { VideoModal } from 'components/VideoModal/VideoModal'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { UserService } from 'api/clients/UserService'
import { generatePath, useHistory } from 'react-router-dom'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UPGRADED_FROM_WORKSPACE_ID } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/VirtualMarketerPlansModal/components/CreditCardForm/CreditCardForm'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { VIRTUAL_MARKETER_ONBOARDING_PATH } from 'paths'
import { VirtualMarketerPreparingModal } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/VirtualMarketerPreparingModal/VirtualMarketerPreparingModal'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useUpdateUserAppPreferences } from 'hooks/queries/useUpdateUserAppPreferences'

const YOUTUBE_EMBED_ID = 'er0Xqh4li54'

export const AutopilotUpgradeModal = ({ isCancellationFlow }: { isCancellationFlow?: boolean }) => {
  const { closeModal, openModal, closeAllModals } = useModals()
  const { trackOnboardingWorkspaceCreated } = useOnboardingTracking()
  const { mutateAsync: updatePreferences } = useUpdateUserAppPreferences()
  const history = useHistory()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspaces = useWorkspaces()
  const { isMobile } = useResponsiveDesign()

  const openVideo = () => {
    openModal(VideoModal, { embedId: YOUTUBE_EMBED_ID }, { newStack: true })
  }

  const thumbnailUrl = `https://img.youtube.com/vi/${YOUTUBE_EMBED_ID}/maxresdefault.jpg`

  const createAutopilotWorkspace = async () => {
    openModal(VirtualMarketerPreparingModal, { showOnboardingLoader: true }, { newStack: true })

    const res = await sendToApi(`/w/${currentWorkspaceId}/virtual_marketers`, {
      body: { trial_period_days: trialDays },
      method: 'POST',
    })

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      localStorage.setItem(UPGRADED_FROM_WORKSPACE_ID, currentWorkspaceId.toString())

      const newWorkspace = res.data.company

      trackOnboardingWorkspaceCreated()

      await updatePreferences({ scheduledPostsView: 'calendar' })

      UserService.setWorkspaces([...workspaces, newWorkspace])
      UserService.setCurrentWorkspaceId(newWorkspace.id)

      closeAllModals()
      history.push(generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, { workspaceId: newWorkspace.id }))
    } else {
      closeModal()
      showToastMessage('An error occurred. Try again. Contact support if the issue persists.', 'error')
    }
  }

  const trialDays = isCancellationFlow ? 14 : 7

  return (
    <Modal.Root onPressOutside={closeModal} bodyClassName={Styles.bodyClassName}>
      <ResponsiveDesign.Mobile>
        <div className={Styles.mobileHeader}>
          <Button variant="ghost" onPress={closeModal} color="var(--light-100)">
            Back
          </Button>
        </div>
        <div className={Styles.socialGroupContainer}>
          <img
            src="https://res.cloudinary.com/almanac/image/upload/v1759371597/blaze_assets/autopilot_upgrade_modal/social_media_group_xybb1v.png"
            alt="Social Media Group"
          />
        </div>
      </ResponsiveDesign.Mobile>
      <Modal.Content className={Styles.modalContent}>
        <div className={Styles.content}>
          <div className={Styles.ctaContainer}>
            <div className={Styles.ctaText}>
              {isCancellationFlow ? (
                <>
                  <h1>Don't quit on marketing – put it on Autopilot instead.</h1>
                  <ResponsiveDesign.Desktop>
                    <h4>Meet the all-new Blaze Autopilot: AI that does marketing for you.</h4>
                  </ResponsiveDesign.Desktop>
                </>
              ) : (
                <>
                  <h1>Upgrade to Autopilot: marketing that runs itself.</h1>
                  <ResponsiveDesign.Desktop>
                    <h4>
                      Start your free {trialDays}-day trial and we’ll create a brand-new Autopilot workspace just for
                      you. Each workspace supports one Brand Kit.
                    </h4>
                  </ResponsiveDesign.Desktop>
                </>
              )}
            </div>
            <div className={Styles.ctaButtonContainer}>
              <div className={Styles.buttonAndText}>
                <Pressable onPress={createAutopilotWorkspace}>
                  <div className={Styles.button}>
                    <Lightning />
                    <span>Start {trialDays}-Day Free Trial</span>
                  </div>
                </Pressable>
                <Text variant="metadata" color={isMobile ? 'var(--light-100)' : 'var(--dark-60)'}>
                  100% free. No card needed. Cancel anytime.
                </Text>
              </div>
              <ResponsiveDesign.Desktop>
                <Button variant="editor" size="xl" onPress={closeModal}>
                  No, Thank You
                </Button>
              </ResponsiveDesign.Desktop>
            </div>
          </div>
          <div className={Styles.imageVideoAndDescriptionContainer}>
            <ResponsiveDesign.Desktop>
              <img
                className={Styles.autopilotHomeImg}
                src="https://res.cloudinary.com/almanac/image/upload/v1759337231/blaze_assets/autopilot_upgrade_modal/autopilot_home_qgebxi.png"
                alt="Autopilot Home"
              />
            </ResponsiveDesign.Desktop>
            <div className={Styles.videoContainer} onClick={openVideo}>
              <img src={thumbnailUrl} alt="Blaze Autopilot Tutorial" className={Styles.videoThumbnail} />
              <div className={Styles.videoPlayContainer}>
                <Play3 color="var(--light-100)" size={isMobile ? 20 : 58} fill="var(--light-100)" />
                <ResponsiveDesign.Mobile>
                  <h4>Play Video</h4>
                </ResponsiveDesign.Mobile>
              </div>
              <ResponsiveDesign.Desktop>
                <div className={Styles.videoDurationContainer}>
                  <span>{durationLabel(119)}</span>
                </div>
              </ResponsiveDesign.Desktop>
            </div>
            <ResponsiveDesign.Desktop>
              <div className={Styles.descriptions}>
                <div className={Styles.description}>
                  <HighDetailedScheduled />
                  <Text color="var(--dark-80)">Creates, schedules, & auto-posts content based on your campaigns</Text>
                </div>
                <div className={Styles.description}>
                  <HighDetailedSocial />
                  <Text color="var(--dark-80)">
                    Delivers your content in weekly batches for quick review and simple editing
                  </Text>
                </div>
                <div className={Styles.description}>
                  <HighDetailedImproveQuality />
                  <Text color="var(--dark-80)">
                    Learns from every post so your business grows faster and attracts more leads
                  </Text>
                </div>
              </div>
            </ResponsiveDesign.Desktop>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
